import React, { useMemo } from 'react';
import Table from '../../components/Table/Table';
import Button from '../../components/Form/Button';
import { FilterBar } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import type { Job } from './types';
import { getStatusBadge } from './types';

interface JobsTabProps {
  jobs: Job[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  departmentFilter: string;
  onDepartmentFilterChange: (value: string) => void;
  locationFilter: string;
  onLocationFilterChange: (value: string) => void;
  typeFilter: string;
  onTypeFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  dateFromFilter: string;
  onDateFromFilterChange: (value: string) => void;
  dateToFilter: string;
  onDateToFilterChange: (value: string) => void;
  onClearFilters: () => void;
  onAddJob: () => void;
  onEditJob: (job: Job) => void;
  onDeleteJob: (id: string) => void;
  onViewJob: (job: Job) => void;
  onApplyToJob: (job: Job) => void;
}

const JobsTab: React.FC<JobsTabProps> = ({
  jobs, searchTerm, onSearchChange,
  departmentFilter, onDepartmentFilterChange,
  locationFilter, onLocationFilterChange,
  typeFilter, onTypeFilterChange,
  statusFilter, onStatusFilterChange,
  dateFromFilter, onDateFromFilterChange,
  dateToFilter, onDateToFilterChange,
  onClearFilters, onAddJob, onEditJob, onDeleteJob, onViewJob, onApplyToJob,
}) => {
  const uniqueDepartments = Array.from(new Set(jobs.map(j => j.department).filter(Boolean)));
  const uniqueLocations = Array.from(new Set(jobs.map(j => j.location).filter(Boolean)));

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = !searchTerm || (
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesDepartment = departmentFilter === 'all' || job.department === departmentFilter;
      const matchesLocation = locationFilter === 'all' || job.location === locationFilter;
      const matchesType = typeFilter === 'all' || job.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
      const postedDate = new Date(job.postedDate);
      const matchesDateFrom = !dateFromFilter || postedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || postedDate <= new Date(dateToFilter);
      return matchesSearch && matchesDepartment && matchesLocation && matchesType &&
             matchesStatus && matchesDateFrom && matchesDateTo;
    });
  }, [jobs, searchTerm, departmentFilter, locationFilter, typeFilter, statusFilter, dateFromFilter, dateToFilter]);

  const handleExport = () => {
    const exportData = filteredJobs.map(job => ({
      'Job ID': job.id,
      'Job Title': job.title,
      Department: job.department,
      Location: job.location,
      Type: job.type,
      'Salary Range': job.salary,
      'Posted Date': job.postedDate,
      Deadline: job.deadline,
      Status: job.status,
      Applicants: job.applicants,
      Description: job.description.substring(0, 100) + '...',
      Requirements: job.requirements.join(', ')
    }));
    exportToExcel(exportData, `job_postings_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = jobs.map(job => ({
      Title: job.title,
      Department: job.department,
      Location: job.location,
      Status: job.status.charAt(0).toUpperCase() + job.status.slice(1),
      'Posted Date': job.postedDate,
      Applicants: job.applicants,
      Description: job.description.substring(0, 100) + '...',
      Requirements: job.requirements.join(', ')
    }));
    printAsExcel(exportData, 'Job Postings');
  };

  const filters: FilterConfig[] = [
    {
      key: 'department', label: 'Department', type: 'select',
      value: departmentFilter, onChange: onDepartmentFilterChange,
      options: [{ value: 'all', label: 'All Departments' }, ...uniqueDepartments.map(d => ({ value: d, label: d }))],
    },
    {
      key: 'location', label: 'Location', type: 'select',
      value: locationFilter, onChange: onLocationFilterChange,
      options: [{ value: 'all', label: 'All Locations' }, ...uniqueLocations.map(l => ({ value: l, label: l }))],
    },
    {
      key: 'type', label: 'Job Type', type: 'select',
      value: typeFilter, onChange: onTypeFilterChange,
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'Full-time', label: 'Full-time' },
        { value: 'Part-time', label: 'Part-time' },
        { value: 'Contract', label: 'Contract' },
        { value: 'Internship', label: 'Internship' },
      ],
    },
    {
      key: 'status', label: 'Status', type: 'select',
      value: statusFilter, onChange: onStatusFilterChange,
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'Open', label: 'Open' },
        { value: 'Closed', label: 'Closed' },
        { value: 'Draft', label: 'Draft' },
      ],
    },
    { key: 'dateFrom', label: 'From Date', type: 'date', value: dateFromFilter, onChange: onDateFromFilterChange },
    { key: 'dateTo', label: 'To Date', type: 'date', value: dateToFilter, onChange: onDateToFilterChange },
  ];

  const columns = [
    { key: 'id', header: 'Job ID', label: 'Job ID' },
    { key: 'title', header: 'Job Title', label: 'Job Title' },
    { key: 'department', header: 'Department', label: 'Department' },
    { key: 'location', header: 'Location', label: 'Location' },
    { key: 'type', header: 'Type', label: 'Type' },
    { key: 'salary', header: 'Salary Range', label: 'Salary Range' },
    {
      key: 'status', header: 'Status', label: 'Status',
      render: (value: string) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(value)}`}>{value}</span>
      ),
    },
    {
      key: 'applicants', header: 'Applicants', label: 'Applicants',
      render: (value: number) => (
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 715.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          {value}
        </div>
      ),
    },
    { key: 'postedDate', header: 'Posted Date', label: 'Posted Date' },
    { key: 'deadline', header: 'Deadline', label: 'Deadline' },
    {
      key: 'actions', header: 'Actions', label: 'Actions',
      render: (_value: any, job: Job) => (
        <div className="flex items-center space-x-2">
          <Button onClick={() => onApplyToJob(job)} variant="primary" size="sm" title="Apply candidate to this job">
            Apply
          </Button>
          <button onClick={() => onEditJob(job)} className="text-blue-600 hover:text-blue-800" title="Edit job">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button onClick={() => onViewJob(job)} className="text-gray-600 hover:text-gray-800" title="View job details">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button onClick={() => onDeleteJob(job.id)} className="text-red-600 hover:text-red-800" title="Delete job">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search jobs..."
        filters={filters}
        onClearFilters={onClearFilters}
        totalCount={jobs.length}
        filteredCount={filteredJobs.length}
        entityName="Job Postings"
        onAdd={onAddJob}
        addLabel="Post New Job"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        data={filteredJobs}
        columns={columns}
        title="Job Postings"
        showAddButton={false}
        showEditButton={false}
        showDeleteButton={false}
        searchable={false}
      />
    </>
  );
};

export default JobsTab;
